import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { staffingPlanAPI } from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  ArrowLeft, LogOut, Sparkles, Loader2, Plus,
  Building2, Users, IndianRupee, ChevronRight, AlertCircle
} from 'lucide-react';

const STATUS_CONFIG = {
  GENERATED: { label: 'New',      color: 'bg-teal-100 text-teal-700'   },
  VIEWED:    { label: 'Viewed',   color: 'bg-blue-100 text-blue-700'   },
  ACTIONED:  { label: 'Actioned', color: 'bg-green-100 text-green-700' },
  EXPIRED:   { label: 'Expired',  color: 'bg-gray-100 text-gray-500'   },
  FAILED:    { label: 'Failed',   color: 'bg-red-100 text-red-700'     },
};

function formatINR(amount) {
  if (!amount) return null;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    notation: 'compact', compactDisplay: 'short'
  }).format(amount);
}

export default function StaffingPlans() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await staffingPlanAPI.list();
      setPlans(res.data.data || []);
    } catch { toast.error('Failed to load staffing plans'); }
    finally { setLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/company/dashboard')}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <img src="/logo-horizontal.png" alt="ShramSena" className="h-7 w-auto" />
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-bold uppercase">Company</span>
          </div>
          <button onClick={() => { logout(); navigate('/company/login'); }}
            className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-slate-800">Staffing Plans</h1>
            <p className="text-sm text-gray-400 mt-0.5">AI-generated plans for your facilities</p>
          </div>
          <button onClick={() => navigate('/company/staffing-plans/generate')}
            className="flex items-center gap-2 bg-teal-600 text-white px-5 py-2.5 rounded-xl
              font-bold text-sm hover:bg-teal-700 transition shadow-lg shadow-teal-500/20">
            <Sparkles className="h-4 w-4" /> Generate New Plan
          </button>
        </div>

        {plans.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Sparkles className="h-8 w-8 text-teal-600" />
            </div>
            <h3 className="font-bold text-gray-500 text-lg">No staffing plans yet</h3>
            <p className="text-sm text-gray-400 mt-1 mb-6 max-w-xs mx-auto">
              Generate your first AI staffing plan to get role recommendations,
              shift distribution and budget estimates.
            </p>
            <button onClick={() => navigate('/company/staffing-plans/generate')}
              className="bg-teal-600 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-teal-700 transition">
              Generate Your First Plan
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {plans.map(plan => {
              const cfg = STATUS_CONFIG[plan.status] || STATUS_CONFIG.GENERATED;
              const isFailed = plan.status === 'FAILED';
              return (
                <div key={plan.id}
                  onClick={() => navigate(`/company/staffing-plans/${plan.id}`)}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6
                    cursor-pointer hover:shadow-md transition">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0
                        ${isFailed ? 'bg-red-50' : 'bg-teal-50'}`}>
                        {isFailed
                          ? <AlertCircle className="h-6 w-6 text-red-500" />
                          : <Sparkles className="h-6 w-6 text-teal-600" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="font-bold text-slate-800">{plan.planName}</h3>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.color}`}>
                            {cfg.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                          <Building2 className="h-3 w-3" />
                          {plan.facilityName}
                          {plan.facilityCity && ` · ${plan.facilityCity}`}
                        </div>
                        {!isFailed && (
                          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                            {plan.totalStaffNeeded != null && (
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                {plan.totalStaffNeeded} staff needed
                              </span>
                            )}
                            {plan.estimatedMonthlyCostMin && (
                              <span className="flex items-center gap-1">
                                <IndianRupee className="h-3 w-3" />
                                {formatINR(plan.estimatedMonthlyCostMin)}
                                {plan.estimatedMonthlyCostMax && ` – ${formatINR(plan.estimatedMonthlyCostMax)}`}
                                /mo est.
                              </span>
                            )}
                          </div>
                        )}
                        {isFailed && (
                          <p className="text-xs text-red-500">{plan.errorMessage}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1.5">
                          {new Date(plan.createdAt).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-300 shrink-0 mt-1" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
